// src/app/accept-invitation/accept-invitation.component.ts

import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InvitationService } from '../../services/invitation.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AcceptedInvitationResponse, InvitationResponse } from '../../models/invitation-response.model';
import { ApplicationService } from '../../services/application.service';
import { InvitationCommunicationService } from '../../services/invitation-communication.service';
import { DataStorageService } from '../../services/data-storage.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-accept-invitation',
    templateUrl: './accept-invitation.component.html',
    styleUrls: ['./accept-invitation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AcceptInvitationComponent implements OnInit, OnDestroy {

  invitationToken: string = '';
  isLoading: boolean = true;
  errorMessage: string = '';
  role: 'borrower' | 'supplier' | 'unknown' = 'unknown';
  isDirect: boolean = false;
  senderAccountId?: number;
  message: string = '';
  private destroy$ = new Subject<void>();
  private inactivityTimer: any;
  private readonly INACTIVITY_TIMEOUT = 300000; // 5 minutes in milliseconds

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invitationService: InvitationService,
    private applicationService: ApplicationService,
    private dataStorageService: DataStorageService,
    private snackBar: MatSnackBar,
    private commService: InvitationCommunicationService
  ) { }

  ngOnInit(): void {
    // NOTE: we deliberately do NOT clear storage here. In-progress application
    // data lives in an encrypted server-side draft and is rehydrated on refresh
    // (see DataStorageService). Clearing on load would defeat resume.
    this.resetInactivityTimer();

    this.invitationToken = this.route.snapshot.paramMap.get('token') || '';
    if (this.invitationToken) {
      this.fetchInvitationDetails(this.invitationToken);
    } else {
      this.errorMessage = 'Invalid invitation token.';
      this.isLoading = false;
      this.showError();
    }

    // Subscribe to submitApplication event
    this.commService.submitApplication$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.submitApplication();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
  }

  @HostListener('document:mousemove')
  @HostListener('document:keypress')
  @HostListener('document:click')
  @HostListener('document:scroll')
  onUserActivity(): void {
    this.resetInactivityTimer();
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    // On inactivity, flush the current progress to the encrypted server-side
    // draft. We intentionally do NOT wipe local data or force a reload — that
    // would destroy the user's in-progress application. The draft persists
    // server-side and can be resumed later on this device.
    this.inactivityTimer = setTimeout(() => {
      this.dataStorageService.flush();
    }, this.INACTIVITY_TIMEOUT);
  }

  /**
   * Fetches the invitation details without accepting it.
   * @param token The invitation token.
   */
  fetchInvitationDetails(token: string): void {
    this.invitationService.getInvitation(token).subscribe({
      next: (response: InvitationResponse | AcceptedInvitationResponse) => {
        if ('isAccepted' in response && response.isAccepted) {
          this.handleAcceptedInvitation(response as AcceptedInvitationResponse);
        } else {
          this.handlePendingInvitation(response as InvitationResponse);
        }
      },
      error: (error) => {
        // Error path - invitation is invalid for some reason
        this.isLoading = false;
        this.errorMessage = error || 'An error occurred while retrieving the invitation.';
        this.showError();
      }
    });
  }

  /**
   * Helper function to handle valid (not yet accepted) invitations
   * 
   * @param response The invitation response from the API
   */
  private async handlePendingInvitation(response: InvitationResponse): Promise<void> {
    // Happy path - only happens if invitation is valid
    this.isLoading = false;
    this.message = response.message || '';
    this.role = toLowerCaseRole(response.role) || 'unknown';
    this.isDirect = response.isDirect || false;
    this.senderAccountId = response.senderAccountId;
    this.commService.role = toLowerCaseRole(response.role) || 'unknown';

    // Draft/server owns applicationGuid (minted on PUT). Invitation check no
    // longer returns a GUID — only persist role + invitation token, then flush
    // so the server can mint/return the durable GUID before Plaid/submit.
    await this.dataStorageService.ready;
    this.dataStorageService.setDraftMeta({
      role: this.commService.role,
      invitationToken: this.invitationToken,
    });
    await this.dataStorageService.flush();

    // Do NOT force-redirect to personal-details here. The child route ''
    // already redirects to personal-details for a bare invite link, and on a
    // refresh of a deeper step the URL itself preserves the user's position —
    // redirecting would throw away their progress.

    // Show success message
    this.snackBar.open(this.message, 'Close', { duration: 5000 });
  }

  /**
   * Helper function to handle already accepted invitations
   * 
   * @param response The accepted invitation response from the API
   */
  private handleAcceptedInvitation(response: AcceptedInvitationResponse): void {
    const status = response.applicationStatus.toLowerCase() ?? null;
    if (!status) {
      this.errorMessage = 'Invalid application status in the accepted invitation.';
      this.showError();
      return;
    }

    // Route to the appropriate application status page with the application state
    this.router.navigate(['application', status], { state: response });
  }

  /**
   * Displays error messages using MatSnackBar or other UI elements.
   */
  showError(): void {
    if (this.errorMessage) {
      this.snackBar.open(this.errorMessage, 'Close', { duration: 5000 });
    }
  }

  /**
   * Collects all data stored in dataStorage by child components
   * and sends it to the API as a single object.
   */
  async submitApplication(): Promise<void> {
    this.isLoading = true;

    // Check if role is valid for application service
    if (this.role === 'unknown') {
      this.isLoading = false;
      this.snackBar.open('Error: Unknown user role.', 'Close', { duration: 3000 });
      return;
    }

    // GUID comes only from draft meta (hydrate/PUT), never from InvitationResponse.
    const applicationGuid = await this.dataStorageService.ensureApplicationGuid();
    if (!applicationGuid) {
      this.isLoading = false;
      this.snackBar.open('Error: Application ID not found. Please try again.', 'Close', { duration: 3000 });
      return;
    }

    // Persist latest cache, then submit the same body shape as draft PUT.
    await this.dataStorageService.flush();
    const draftBody = this.dataStorageService.getDraftBody();
    const validRole = this.role as 'borrower' | 'supplier';
    console.log('Submitting application draft:', draftBody);

    this.applicationService.updateApplication(validRole, draftBody).subscribe({
      next: () => {
        // Happy path - application updated and invitation accepted
        this.isLoading = false;
        this.snackBar.open('Application updated and invitation accepted successfully!', 'Close', { duration: 3000 });

        // Navigate to thank you page. The draft is discarded there, AFTER the
        // thank-you page has read the details it needs to display.
        this.router.navigate(['thank-you'], { relativeTo: this.route });
      },
      error: (error) => {
        console.error('Error updating application:', error);
        this.isLoading = false;
        this.snackBar.open('An error occurred while updating your application.', 'Close', { duration: 3000 });
      }
    });
  }
}

const toLowerCaseRole = (role: 'BORROWER' | 'SUPPLIER'): 'borrower' | 'supplier' => {
  return role.toLowerCase() as 'borrower' | 'supplier';
};
